const RELATIVE_TIME_FORMATTER_KR = new Intl.RelativeTimeFormat("ko-KR", {
  numeric: "auto",
});

const RELATIVE_TIME_FORMATTER_US = new Intl.RelativeTimeFormat("en-US", {
  numeric: "auto",
});

const DATE_FORMATTER_FULL_US = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const DATE_FORMATTER_FULL_KR = new Intl.DateTimeFormat("ko-KR", {
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const DATE_FORMATTER_YEAR_US = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const DATE_FORMATTER_YEAR_KR = new Intl.DateTimeFormat("ko-KR", {
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const DATE_FORMATTER_MONTH_US = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const DATE_FORMATTER_MONTH_KR = new Intl.DateTimeFormat("ko-KR", {
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const DATE_FORMATTER_DAY_US = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  minute: "2-digit",
});

const DATE_FORMATTER_DAY_KR = new Intl.DateTimeFormat("ko-KR", {
  hour: "2-digit",
  minute: "2-digit",
});

export const timestampToDate = (timestamp: string) => {
  let date: Date;

  // 숫자 문자열인지 확인
  if (/^\d+$/.test(timestamp)) {
    date = new Date(parseInt(timestamp, 10));
  } else {
    // ISO 문자열로 처리
    date = new Date(timestamp);
  }

  return date;
};

export const dateTimeFormat = (
  timestamp: string,
  locales: Intl.LocalesArgument = "ko-KR",
) => {
  const date = timestampToDate(timestamp);
  // date가 유효한지 확인
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return "날짜 형식 오류";
  }
  return locales === "ko-KR"
    ? DATE_FORMATTER_FULL_KR.format(date)
    : DATE_FORMATTER_FULL_US.format(date);
};

export function formatDate(
  timestamp: string,
  format: string = "YYYY-MM-DD HH:mm:ss",
): string {
  const date = timestampToDate(timestamp);
  // date가 유효한지 확인
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return "날짜 형식 오류";
  }
  const formatOptions: { [key: string]: string | number } = {
    YYYY: date.getFullYear(),
    MM: String(date.getMonth() + 1).padStart(2, "0"),
    DD: String(date.getDate()).padStart(2, "0"),
    HH: String(date.getHours()).padStart(2, "0"),
    mm: String(date.getMinutes()).padStart(2, "0"),
    ss: String(date.getSeconds()).padStart(2, "0"),
    YY: date.getFullYear().toString().slice(-2),
    M: date.getMonth() + 1,
    D: date.getDate(),
    H: date.getHours(),
    m: date.getMinutes(),
    s: date.getSeconds(),
  };

  return format.replace(/YYYY|MM|DD|HH|mm|ss|YY|M|D|H|m|s/g, (match) =>
    String(formatOptions[match]),
  );
}

export function formatRelativeTime(
  timestamp: string,
  locales: Intl.LocalesArgument = "ko-KR",
): string {
  const date = timestampToDate(timestamp);
  // date가 유효한지 확인
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return "날짜 형식 오류";
  }

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const formatter =
    locales === "ko-KR"
      ? RELATIVE_TIME_FORMATTER_KR
      : RELATIVE_TIME_FORMATTER_US;

  // 1분 미만
  if (diffInSeconds < 60) {
    return formatter.format(-Math.floor(diffInSeconds), "seconds");
  }

  // 1시간 미만
  const minutes = Math.floor(diffInSeconds / 60);
  if (minutes < 60) {
    return formatter.format(-minutes, "minutes");
  }

  // 24시간 미만
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return formatter.format(-hours, "hours");
  }

  // 30일 미만
  const days = Math.floor(hours / 24);
  if (days < 30) {
    return formatter.format(-days, "days");
  }

  // 12개월 미만
  const months = Math.floor(days / 30);
  if (months < 12) {
    return formatter.format(-months, "months");
  }

  // 1년 이상
  const years = Math.floor(months / 12);
  return formatter.format(-years, "years");
}

export function formatChatDate(
  timestamp: string,
  locales: Intl.LocalesArgument = "ko-KR",
): string {
  const date = timestampToDate(timestamp);
  // date가 유효한지 확인
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return "날짜 형식 오류";
  }

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const formatter =
    locales === "ko-KR"
      ? RELATIVE_TIME_FORMATTER_KR
      : RELATIVE_TIME_FORMATTER_US;

  // 절대적인 시간.
  const absoluteDateTimeDay =
    locales === "ko-KR"
      ? DATE_FORMATTER_DAY_KR.format(date)
      : DATE_FORMATTER_DAY_US.format(date);

  // 1분 미만
  if (diffInSeconds < 60) {
    // return `${formatter.format(-Math.floor(diffInSeconds), "seconds")}(${absoluteDateTimeDay})`;
    const justNow = locales === "ko-KR" ? "방금 전" : "just now";
    return `${justNow}(${absoluteDateTimeDay})`;
  }

  // 1시간 미만
  const minutes = Math.floor(diffInSeconds / 60);
  if (minutes < 60) {
    return `${formatter.format(-minutes, "minutes")}(${absoluteDateTimeDay})`;
  }

  // 24시간 미만
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${formatter.format(-hours, "hours")}(${absoluteDateTimeDay})`;
  }

  // 절대적인 시간.
  const absoluteDateTimeMonth =
    locales === "ko-KR"
      ? DATE_FORMATTER_MONTH_KR.format(date)
      : DATE_FORMATTER_MONTH_US.format(date);

  // 30일 미만
  const days = Math.floor(hours / 24);
  if (days < 30) {
    return `${formatter.format(-days, "days")}(${absoluteDateTimeMonth})`;
  }

  // 절대적인 시간.
  const absoluteDateTimeYear =
    locales === "ko-KR"
      ? DATE_FORMATTER_YEAR_KR.format(date)
      : DATE_FORMATTER_YEAR_US.format(date);

  // 12개월 미만
  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${formatter.format(-months, "months")}(${absoluteDateTimeYear})`;
  }

  // 절대적인 시간.
  const absoluteDateTimeFull =
    locales === "ko-KR"
      ? DATE_FORMATTER_FULL_KR.format(date)
      : DATE_FORMATTER_FULL_US.format(date);

  // 1년 이상
  const years = Math.floor(months / 12);
  return `${formatter.format(-years, "years")}(${absoluteDateTimeFull})`;
}
